import { useContext, useEffect, useRef, useState } from 'react';
import useSmartState from '../../../common/hooks/useSmartState';
import { SmartContext } from '../../../library/Core/SmartContext';
import SessionContext from '../../../library/Core/SessionContext';
import AlumniDetailsModal from './AlumniDetailsModal';
import { isEmpty, getDomainValueForCode, getActionElementClass, allowPerformAction, displayActionNotAllowedAlert, canExportToExcel, exportToExcel } from '../../../library/Core/SmartFunctions';
import BusinessDirectoryService from './BusinessDirectoryService';
import ComposeEmailModal from './ComposeEmailModal';
import { toast } from 'react-toastify';
import AddServiceProviderModal from './AddServiceProviderModal';
import { getStudentImage } from '../../../common/styles/Images';
import InitialsAvatar, { hasProfilePicture } from '../../../library/SimpleControls/InitialsAvatar';

const Results = () => {
    const { state } = useContext(SmartContext);
    const { setStateValue } = useSmartState();
    const { sessionState } = useContext(SessionContext);
    const isAlumni = sessionState?.userTypeCode === 'ALUMNI';
    const isSuperAdmin = sessionState?.role === 'SUPER_ADMIN' || sessionState?.userTypeCode === 'SUPER_ADMIN';
    const canSendEmail = !isSuperAdmin;
    const showExportButton = canExportToExcel(sessionState);
    const handleExportToExcel = () => exportToExcel(providerList, 'business-directory', state);
    const viewMode = state.data?.viewMode ?? 'list';
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const providerList = state.data?.providerList ?? [];
    const pagination = state.data?.pagination ?? { total: 0, page: 1, limit: 20 };
    const hasMore = state.data?.hasMore ?? false;
const handleSettingClick = (providerId: string) => {
        setOpenDropdown(openDropdown === providerId ? null : providerId);
    };
 
const [openDropdown, setOpenDropdown] = useState<string | null>(null);
 
    const loadMore = async () => {
        if (isLoadingMore || !hasMore) return;

        const nextPage = (pagination.page || 1) + 1;
        setIsLoadingMore(true);

        try {
            const response = await BusinessDirectoryService.search({
                ...state.data.searchCriteria,
                page: nextPage,
                limit: pagination.limit || 20,
            });

            const payload = response?.data ?? {};
            const rows = payload?.data ?? [];

            const merged = [...providerList, ...rows].filter(
                (item, index, arr) => index === arr.findIndex((x) => x.providerId === item.providerId)
            );

            setStateValue('providerList', merged as any);
            setStateValue('pagination', {
                total: payload?.total ?? pagination.total,
                page: payload?.page ?? nextPage,
                limit: payload?.limit ?? pagination.limit,
            } as any);
            setStateValue(
                'hasMore',
                ((payload?.page ?? nextPage) * (payload?.limit ?? pagination.limit)) < (payload?.total ?? pagination.total) as any
            );
            setStateValue('searchCriteria.page', (payload?.page ?? nextPage) as any);
        } catch (error) {
            console.error('Pagination load error:', error);
            toast.error('Unable to load more providers');
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        const refNode = loadMoreRef.current;
        if (!refNode) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
                    loadMore();
                }
            },
            { threshold: 0.25 }
        );

        observer.observe(refNode);

        return () => observer.disconnect();
    }, [hasMore, isLoadingMore, pagination.page, pagination.total, providerList.length]);

    const refreshList = () => {
        BusinessDirectoryService.search(state.data?.searchCriteria || {}).then((response) => {
            const payload = response?.data ?? {};
            setStateValue('providerList', payload?.data ?? []);
            setStateValue('pagination', {
                total: payload?.total ?? 0,
                page: payload?.page ?? 1,
                limit: payload?.limit ?? 20,
            } as any);
        });
    };

    const fetchProviderDetail = async (providerId: string) => {
        if (!allowPerformAction()) {
            displayActionNotAllowedAlert();
            return;
        }

        try {
            const response = await BusinessDirectoryService.getProviderDetail(providerId);
            setEditData(response?.data || null);
        } catch (err) {
            console.error('Error fetching provider detail:', err);
            toast.error('Failed to load provider details');
        }
    };

    const handleExportCSV = async () => {
        if (isExporting || isAlumni) return;
        setIsExporting(true);
        try {
            await BusinessDirectoryService.exportCSV(state.data.searchCriteria);
            toast.success('CSV exported successfully');
        } catch (error: any) {
            console.error('Export error:', error);
            // Handle row limit exceeded (400 from backend)
            const message = error?.response?.data?.message;
            if (message) {
                toast.error(message);
            } else {
                toast.error('Failed to export CSV. Please try again.');
            }
        } finally {
            setIsExporting(false);
        }
    };

    const renderEmptyState = () => (
        <div className="empty-state text-center p-5">
            <i className="icon-inbox font-48 text-muted d-block mb-3"></i>
            <h5 className="font-16 font-600">No providers found</h5>
            <p className="text-muted">Try adjusting your filters or search criteria</p>
        </div>
    );

    const getAvailabilityBadgeClass = (availability: string) => {
        switch(availability?.toUpperCase()) {
            case 'AVAILABLE':
                return 'bg-success-subtle bg-opacity-10 text-success';
            case 'NOT_AVAILABLE':
                return 'bg-danger-subtle bg-opacity-10 text-danger';
            case 'BUSY':
                return 'bg-secondary-subtle bg-opacity-10 text-secondary';
            default:
                return 'bg-secondary-subtle bg-opacity-10 text-secondary';
        }
    };

    const getAvailabilityLabel = (availability: string) => {
        switch(availability?.toUpperCase()) {
            case 'AVAILABLE':
                return 'Available';
            case 'NOT_AVAILABLE':
                return 'Not Available';
            case 'BUSY':
                return 'Busy';
            default:
                return availability || 'Unknown';
        }
    };

    const renderListView = () => (
        <div className="table-responsive attendee_dtl">
            <table className="table" style={{ minWidth: '1024px' }}>
                <thead>
                    <tr>
                        <th>Alumni</th>
                        <th>Service Category</th>
                        <th>Business Name</th>
                        <th>Location</th>
                        <th>Experience</th>
                        <th>Availability</th>
                        <th className="text-end"><div className="labelItem justify-content-end pe-2">Action</div></th>
                    </tr>
                </thead>
                <tbody>
                    {providerList.map((provider: any, index: number) => (
                        <tr key={`${provider.providerId}-${index}`}>
                            <td className="align-middle">
                                <div className="d-flex checkoption">
                                    <a className="tablink align-items-center" href="javascript:;">
                                        <i className="me-2 position-relative">
                                            <i className="bi bi-check-circle-fill font-16 me-1 text-green position-absolute"></i>
                                            {hasProfilePicture(provider.imagePath) ? (
                                                <img src={getStudentImage({ profilePictureFilePath: provider.imagePath, genderCode: provider.genderCode })} className="rounded-circle img-fluid" width="40" height="40" alt={provider.alumniName} style={{ objectFit: 'cover' }} />
                                            ) : (
                                                <InitialsAvatar name={provider.alumniName} size={40} />
                                            )}
                                        </i>
                                        <span className="d-flex flex-column">
                                            <span className="singlelinename w-100 font-16 lineHeight-16 font-500" title={provider.alumniName}>
                                                {provider.alumniName}
                                            </span>
                                            <span>{provider.programName || 'Program'}, {provider.passOutYear || 'N/A'}</span>
                                        </span>
                                    </a>
                                </div>
                            </td>
                            <td className="align-middle">
                                <div className="d-flex flex-column">
                                    <div className="font-500">
                                        {(getDomainValueForCode(provider?.category, 'BUSINESS_CATEGORY', state) ?? provider?.category) || 'N/A'}
                                    </div>
                                    {/* <div className="font-400">{provider.businessName}</div> */}
                                </div>
                            </td>
                            <td className="align-middle">
                                <div className="d-flex flex-column">
                                    <div className="font-400">{provider.businessName}</div>
                                </div>
                            </td>
                            <td className="align-middle">
                                <div className="d-flex flex-column">
                                    <div className="font-400">
                                        {provider?.city && (getDomainValueForCode(provider?.city, 'CITY_CODE', state) ?? '')}
                                        {provider?.city && provider?.country && ', '}
                                        {provider?.country && (getDomainValueForCode(provider?.country, 'COUNTRY_CODE', state) ?? '')}
                                    </div>
                                </div>
                            </td>
                            <td className="align-middle">
                                <div className="d-flex flex-column">
                                    <div className="font-400">{provider.experience ? `${provider.experience} years` : '-'}</div>
                                </div>
                            </td>
                            <td className="align-middle">
                                <div className="d-flex flex-column align-items-center">
                                    <span className={`badge rounded-pill font-14 font-500 px-3 py-2 max-120 w-100 ${getAvailabilityBadgeClass(provider.availability)}`}>
                                        {getAvailabilityLabel(provider.availability)}
                                    </span>
                                </div>
                            </td>
                            
                                <td className="text-center align-middle">
                                    <div className="d-flex justify-content-center">
                                        <div className="d-flex flex-wrap flex-column mailSetting">
                                            <div className="setting">
                                              <a
                                                    className="icon-setting font-20 text-decoration-none"
                                                    href="javascript:;"
                                                    onClick={() => handleSettingClick(provider.providerId)}
                                                    style={{ cursor: 'pointer' }}
                                                ></a>
                                            </div>
                                            <div className={`setting-dropdown ${openDropdown === provider.providerId ? 'open' : ''}`}>
                                                <ul>
                                                   <li><AlumniDetailsModal className="dropdown-item font-500"  modalTitle='View' providerId={provider.providerId}/></li>
                                                   {canSendEmail && <li><ComposeEmailModal className="dropdown-item font-500" btnTitle={'Email'} provider={provider} iconMain={undefined}/></li>}
                                                   {(!isAlumni || provider.userId === sessionState.userId) && (
                                                       <li><a href="javascript:;" className={`dropdown-item font-500 ${getActionElementClass()}`} onClick={() => { setOpenDropdown(null); fetchProviderDetail(provider.providerId); }}> Edit Details</a></li>
                                                   )}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            
                        </tr>
                    ))}
                </tbody>
            </table>
            {!isLoadingMore && providerList.length === 0 && renderEmptyState()}
        </div>
    );

    const renderGridView = () => (
        <div className="row g-4">
            {providerList.map((provider: any, index: number) => (
                <div key={`${provider.providerId}-${index}`} className="col-sm-6 col-md-6 col-lg-4">
                    <div className="business-card border-radius-6 h-100 d-flex flex-column border-0B3454 overflow-hidden">
                        {/* Card header â€” alumni photo + name + graduation */}
                        <div className="bg-darker-blue text-white p-4">
                            <div className="row g-3 align-items-center mb-4 mb-md-0">
                                <div className="col-auto">
                                    <div className="alumniDetailPic ratio-60 position-relative d-flex justify-content-center align-items-center">
                                        <i className="bi bi-check-circle-fill font-16 me-1 text-green position-absolute" style={{ top: '-6px', right: '-6px' }}></i>
                                        {hasProfilePicture(provider.imagePath) ? (
                                            <img src={getStudentImage({ profilePictureFilePath: provider.imagePath, genderCode: provider.genderCode })} className="rounded-circle" width="60" height="60" alt={provider.alumniName} style={{ objectFit: 'cover' }} />
                                        ) : (
                                            <InitialsAvatar name={provider.alumniName} size={60} />
                                        )}
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="d-flex flex-column">
                                        <span className="font-18 font-500 line-height-24">{provider.alumniName}</span>
                                        <span className="font-14 font-400">
                                            {[provider.programName, provider.passOutYear].filter(Boolean).join(', ') || '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Card body â€” business name, location, category + subcategory */}
                        <div className="flex-1 p-4">
                            <div className="font-16 font-400 mb-2 d-flex flex-wrap">
                                <i className="bi bi-buildings font-18 me-2"></i>
                                <span className="flex-1">{provider.businessName || '-'}</span>
                            </div>
                            <div className="font-16 font-400 mb-2 d-flex flex-wrap">
                                <i className="bi bi-geo-alt-fill font-18 me-2"></i>
                                <span className="flex-1">
                                    {[provider?.city && (getDomainValueForCode(provider.city, 'CITY_CODE', state) ?? provider.city),
                                      provider?.country && (getDomainValueForCode(provider.country, 'COUNTRY_CODE', state) ?? provider.country)
                                    ].filter(Boolean).join(', ') || '-'}
                                </span>
                            </div>
                            <div className="font-16 font-400 d-flex flex-wrap">
                                <i className="icon-brife-case font-18 me-2"></i>
                                <span className="flex-1">
                                    <b className="font-500 d-block">
                                        {(getDomainValueForCode(provider?.category, 'BUSINESS_CATEGORY', state) ?? provider?.category) || '-'}
                                    </b>
                                    {provider.subcategory || ''}
                                </span>
                            </div>
                        </div>

                        {/* Card footer â€” experience + actions */}
                        <div className="bg-gray-f5 px-4 py-3">
                            <div className="row g-2">
                                <div className="col font-16">
                                    {provider.experience ? `${provider.experience} years` : '-'}
                                </div>
                                <div className="col-auto">
                                    <div className="d-flex align-items-center">
                                        <AlumniDetailsModal className="font-16 font-500 text-dark-blue text-decoration-none me-3" modalTitle='View Details' providerId={provider.providerId}/>
                                        {canSendEmail && <ComposeEmailModal btnTitle={'Email'} iconMain={<i className="icon-mail"></i>} className="font-16 font-500 text-dark-blue text-decoration-none me-3" provider={provider}/>}
                                        {(!isAlumni || provider.userId === sessionState.userId) && (
                                            <a href="javascript:;" 
                                                className={`font-16 font-500 text-dark-blue text-decoration-none ${getActionElementClass()}`} 
                                                onClick={() => fetchProviderDetail(provider.providerId)}>Edit</a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            {!isLoadingMore && providerList.length === 0 && renderEmptyState()}
        </div>
    );

    return (
        <div className="results-section">
            {/* Header with View Toggle and Export */}
            <div className="results-header d-flex justify-content-between align-items-center mb-4 gap-3">
                {/* <div className="view-toggle">
                    <button
                        className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setStateValue('viewMode', 'list' as any)}
                    >
                        <i className="icon-list me-2"></i>List
                    </button>
                    <button
                        className={`btn btn-sm ms-2 ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setStateValue('viewMode', 'grid' as any)}
                    >
                        <i className="icon-grid me-2"></i>Grid
                    </button>
                </div> */}

                <div className="results-count">
                    <span className="text-muted">
                        Showing {providerList.length} of {pagination.total} provider{pagination.total !== 1 ? 's' : ''}
                    </span>
                </div>

                {showExportButton && (
                    <button
                        className="btn btn-sm btn-success"
                        onClick={handleExportToExcel}
                        disabled={isEmpty(providerList)}
                    >
                        Export to Excel
                    </button>
                )}

                {/* {!isAlumni && (
                    <button
                        className="btn btn-sm btn-success"
                        onClick={handleExportCSV}
                        disabled={isEmpty(providerList) || isExporting}
                    >
                        {isExporting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Exporting...
                            </>
                        ) : (
                            <><i className="icon-download me-2"></i>Export CSV</>
                        )}
                    </button>
                )} */}
            </div>

            {/* Results Display */}
            <div className="results-body">
                {isEmpty(providerList) ? (
                    renderEmptyState()
                ) : viewMode === 'list' ? (
                    renderListView()
                ) : (
                    renderGridView()
                )}

                {!isEmpty(providerList) && <div ref={loadMoreRef} style={{ height: 1 }} />}

                {isLoadingMore && (
                    <div className="text-center py-3 text-muted">
                        Loading more providers...
                    </div>
                )}
            </div>

            {/* Edit modal â€” opens when Edit Details is clicked */}
            {editData && (
                <AddServiceProviderModal
                    editData={editData}
                    onEditClose={() => {
                        setEditData(null);
                        refreshList();
                    }}
                />
            )}
        </div>
    );
};

export default Results;
